import React from 'react'
import { ConfigurationContext } from './configuration-context'
import { getClassNames } from '../../../_utils/classnames'
import { Well } from '@pushly/aqe/lib/components'
import { QuestionCircleOutlined } from '@ant-design/icons'
import { extractJourneyTrigger } from '../../../_utils/journeys'
import moment, { Moment } from 'moment'
import { BetterTimePicker } from '@pushly/aqe/lib/components/better-timepicker/better-timepicker'
import { TriggerType } from '@pushly/models/lib/enums/journeys'
import { Flex, Popover, Skeleton, Space, Switch } from 'antd'
import dayjs from 'dayjs'

interface ISendSettingsWell {
    submitConfiguration?: Record<string, unknown>
}

interface IState {}

export class SendSettingsWell extends React.Component<ISendSettingsWell, IState> {
    public static contextType = ConfigurationContext
    public context!: React.ContextType<typeof ConfigurationContext>

    public state: IState = {}

    public render() {
        const { mode, loading, journey, flags } = this.context

        let quietHoursStart
        if (journey.configuration.quiet_hours?.start) {
            quietHoursStart = moment(journey.configuration.quiet_hours.start, 'hh:mm A')
        }

        let quietHoursEnd
        if (journey.configuration.quiet_hours?.end) {
            quietHoursEnd = moment(journey.configuration.quiet_hours.end, 'hh:mm A')
        }

        const trigger = extractJourneyTrigger(journey)
        const triggerConfig = trigger?.configuration
        const triggerType = triggerConfig?.type
        const isSubscribedTrigger = triggerType === TriggerType.SUBSCRIBED

        return (
            <Well
                className={getClassNames(null, 'journey-send-settings-well', 'nested', `mode-${mode}`, {
                    ['final-well']: !!this.props.submitConfiguration,
                })}
                title="Send Settings"
                footerMode={this.props.submitConfiguration ? 'ghost' : 'hidden'}
                {...this.props.submitConfiguration}
            >
                <Skeleton
                    loading={loading}
                    active={true}
                    title={false}
                    paragraph={{
                        rows: 1,
                        width: '100%',
                    }}
                >
                    {!isSubscribedTrigger && (
                        <Well
                            variant="ghost"
                            title={
                                <>
                                    <span>Quiet Hours</span>
                                    <Popover
                                        classNames={{
                                            content: 'quiet-hours-overlay',
                                        }}
                                        content={
                                            <p>
                                                Enabling Quiet Hours delays notifications from being sent during the
                                                chosen timeframe in the subscriber’s time zone. Any notifications that
                                                would have been sent during this time will be sent in the first hour
                                                after Quiet Hours ends.
                                            </p>
                                        }
                                    >
                                        <QuestionCircleOutlined className="info-icon" />
                                    </Popover>
                                </>
                            }
                            footerMode="hidden"
                            padding="none"
                        >
                            <Flex vertical gap={8}>
                                <Space align="center">
                                    <Switch
                                        id="jc-quiet-hours-switch"
                                        size="small"
                                        checked={flags.quietHoursEnabled ?? false}
                                        onChange={this.handleQuietHoursEnabledChange}
                                        aria-label="Enable Quiet Hours"
                                    />
                                    <label htmlFor="jc-quiet-hours-switch">Enable Quiet Hours</label>
                                </Space>

                                {flags.quietHoursEnabled && (
                                    <div className="quiet-hours-wrapper">
                                        <span>Do not send notifications between</span>
                                        <BetterTimePicker
                                            className="quiet-hours-start-time"
                                            size="small"
                                            placeholder="Start time"
                                            use12Hours={true}
                                            format="hh:mm A"
                                            value={quietHoursStart}
                                            onTimeChange={this.handleQuietHoursStartChange}
                                        />
                                        <span> and </span>
                                        <BetterTimePicker
                                            className="quiet-hours-end-time"
                                            size="small"
                                            placeholder="End time"
                                            use12Hours={true}
                                            format="hh:mm A"
                                            value={quietHoursEnd}
                                            onTimeChange={this.handleQuietHoursEndChange}
                                        />
                                        <span> in subscriber’s time zone.</span>
                                    </div>
                                )}
                            </Flex>
                        </Well>
                    )}
                </Skeleton>
            </Well>
        )
    }

    protected handleQuietHoursEnabledChange = (quietHoursEnabled: boolean) => {
        const updated = structuredClone(this.context.journey)

        if (!quietHoursEnabled) {
            updated.configuration.quiet_hours = undefined
            this.context.setJourney(updated)
        }

        this.context.setFlags({ quietHoursEnabled })
    }

    protected handleQuietHoursStartChange = (start: dayjs.Dayjs | null) => {
        const updated = structuredClone(this.context.journey)

        updated.configuration.quiet_hours = {
            start: start?.format('HH:mm') ?? '',
            end: updated.configuration.quiet_hours?.end ?? '',
        }

        this.context.setJourney(updated)
    }

    protected handleQuietHoursEndChange = (end: dayjs.Dayjs | null) => {
        const updated = structuredClone(this.context.journey)

        updated.configuration.quiet_hours = {
            start: updated.configuration.quiet_hours?.start ?? '',
            end: end?.format('HH:mm') ?? '',
        }

        this.context.setJourney(updated)
    }
}
